/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'

import {useProductList} from '../util/product-list-context'

// Components
import {
    Box,
    Heading,
    Stack,
    Text,
    Skeleton
} from '@salesforce/retail-react-app/app/components/shared/ui'
import ReadMoreText from '../../../components/read-more-text'

// Project Components
import Breadcrumb from '../../../components/breadcrumb'

/**
 * PageHeader component is responsible for rendering the header section of the product list page.
 * It utilizes the `useProductList` hook to retrieve necessary data.
 *
 * @param {Object} props - The component props
 * @param {Object} props.containerProps - container prop
 *
 * @returns {JSX.Element} The rendered header section of the product list page, including breadcrumb,
 * category title, and description.
 */
const PageHeader = ({containerProps = {}, descriptionProps = {}, isFamily = false}) => {
    const {searchQuery, category} = useProductList()

    return (
        <Box data-testid="sf-product-list-breadcrumb">
            {/* Breadcrumb */}
            {category && <Breadcrumb categories={category?.c_extendedParentCategoryTree} />}

            {/* Category Header */}
            <Stack gap={12} px={[10, 10, 20, 20]} py={[45, 45, 35, 35]} {...containerProps}>
                {isFamily ? (
                    <Heading as="h1" fontSize={['4xl', null, '5xl']} textTransform="uppercase">
                        <FormattedMessage
                            defaultMessage="Family prints"
                            id="product_list.header.family_prints"
                        />
                    </Heading>
                ) : (
                    <>
                        {searchQuery && (
                            <Text textStyle="eyeBrowLarge" color="darkGray" textTransform="uppercase">
                                <FormattedMessage
                                    defaultMessage="Search Results for"
                                    id="product_list.header.search_for"
                                />
                            </Text>
                        )}

                        {/* Category Title */}
                        {category || searchQuery ? (
                            <Heading as="h1" fontSize={['4xl', null, '5xl']} textTransform="uppercase">
                                {`${category?.c_seoDisplayName || category?.name || searchQuery || ''}`}
                            </Heading>
                        ) : (
                            <Skeleton height="52px" />
                        )}

                        {/* Category Description */}
                        {category?.description && (
                            <ReadMoreText
                                description={category.description}
                                shortDescription={category.c_shortDescription}
                                maxWidth={{base: '330px', md: '520px'}}
                                {...descriptionProps}
                            />
                        )}
                    </>
                )}
            </Stack>
        </Box>
    )
}

PageHeader.propTypes = {containerProps: PropTypes.object, descriptionProps: PropTypes.object, isFamily: PropTypes.bool}

export default PageHeader
